import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save } from 'lucide-react';
import Layout from '../../components/Layout';
import { userService, UserDTO, RoleDTO } from '../../services/userService';
import { outletService } from '../../services/outletService';
import { useModule } from '../../context/ModuleContext';
import { APP_LABELS, APP_ICONS } from '../../context/AppAccessContext';
import { parseApiError } from '../../utils/errorUtils';
import { rolesRequireOutlet } from '../../utils/outletRequirement';
import '../../styles/ImsTheme.css';
import '../../pages/Products/ProductCategoryCreatePage.css';

interface OutletOption {
  id: number;
  name: string;
}

interface EditUserFormData {
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  roleIds: number[];
  outletId: number | null;
}

const UserEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { company } = useModule();
  const [user, setUser] = useState<UserDTO | null>(null);
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [companyApps, setCompanyApps] = useState<string[]>([]);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [outlets, setOutlets] = useState<OutletOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<EditUserFormData>({
    email: '',
    firstName: '',
    lastName: '',
    isActive: true,
    roleIds: [],
    outletId: null,
  });

  // PIN management — separate from the main form since it's its own endpoint.
  const [pinValue, setPinValue] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinSuccess, setPinSuccess] = useState(false);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, company?.id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const requests: Promise<any>[] = [
        userService.getUserById(id || '0'),
        userService.getRoles(),
        userService.getUserApps(id || '0'),
        outletService.getAll(1, 100),
      ];
      if (company?.id) requests.push(userService.getCompanyApps(company.id));

      const [userResponse, rolesResponse, userAppsResponse, outletsResponse, companyAppsResponse] =
        await Promise.all(requests);

      if (userResponse?.data?.data) {
        const u = userResponse.data.data;
        setUser(u);
        setFormData({
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          isActive: u.isActive,
          roleIds: u.roles ? u.roles.map((r: any) => r.id) : [],
          outletId: u.outletId ?? null,
        });
      }

      const outletsData = (outletsResponse?.data as any)?.data?.data ?? (outletsResponse?.data as any)?.data;
      if (Array.isArray(outletsData)) setOutlets(outletsData);

      const rolesData = rolesResponse?.data?.data;
      if (Array.isArray(rolesData)) setRoles(rolesData);

      const userAppsData = userAppsResponse?.data?.data;
      if (Array.isArray(userAppsData)) setSelectedApps(userAppsData);

      if (companyAppsResponse) {
        const companyAppsData = companyAppsResponse?.data?.data;
        if (Array.isArray(companyAppsData)) setCompanyApps(companyAppsData);
      }

      setLoading(false);
    } catch {
      setError(t('user.failedToLoad'));
      setLoading(false);
    }
  };

  const handleAppToggle = (appCode: string) => {
    setSelectedApps((prev) =>
      prev.includes(appCode) ? prev.filter((a) => a !== appCode) : [...prev, appCode]
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleRoleToggle = (roleId: number) => {
    setFormData((prev) => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter((rid) => rid !== roleId)
        : [...prev.roleIds, roleId],
    }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    if (!formData.email.trim()) {
      setError(t('user.formValidation.emailRequired'));
      return false;
    }
    if (!validateEmail(formData.email)) {
      setError(t('user.formValidation.emailInvalid'));
      return false;
    }
    if (!formData.firstName.trim()) {
      setError(t('user.formValidation.firstNameRequired'));
      return false;
    }
    if (!formData.lastName.trim()) {
      setError(t('user.formValidation.lastNameRequired'));
      return false;
    }
    if (rolesRequireOutlet(roles, formData.roleIds) && !formData.outletId) {
      setError(t('user.formValidation.outletRequired'));
      return false;
    }
    return true;
  };

  const handleSetPin = async () => {
    if (!user) return;
    if (!/^\d{4,6}$/.test(pinValue)) {
      setPinError('PIN must be 4 to 6 digits.');
      return;
    }
    setPinSaving(true);
    setPinError(null);
    setPinSuccess(false);
    try {
      await userService.setPin(user.id, pinValue);
      setPinSuccess(true);
      setPinValue('');
      setUser((prev) => (prev ? { ...prev, hasPin: true } : prev));
    } catch (err: unknown) {
      setPinError(parseApiError(err, 'Failed to set PIN.').message);
    } finally {
      setPinSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!validateForm() || !user) return;

    setSaving(true);
    try {
      await Promise.all([
        userService.updateUser(user.id, {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          isActive: formData.isActive,
          roleIds: formData.roleIds,
          outletId: formData.outletId,
        }),
        userService.setUserApps(user.id, selectedApps),
      ]);
      navigate(`/users/${user.id}`, { state: { message: t('user.userUpdatedSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('user.failedToUpdate')).message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="create-container">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="create-container">
          <div className="alert alert-danger">{error || t('user.userNotFound')}</div>
          <button className="create-breadcrumb-btn" onClick={() => navigate('/users')}>
            {t('user.backToUsers')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="create-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate(`/users/${id}`)}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('user.editTitle')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate(`/users/${id}`)} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.saveChanges')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="create-content">
          <div className="create-main-section">
            <div className="create-card">
              <div className="create-card-header">
                <h2 className="create-card-title">{t('user.accountInfo')}</h2>
              </div>
              <div className="create-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">{t('user.username')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={user.username}
                      disabled
                      style={{ opacity: 0.6, cursor: 'not-allowed' }}
                    />
                    <small style={{ fontSize: '12px', color: '#6b7280' }}>
                      {t('user.username')} {t('common.required').toLowerCase().includes('field') ? '' : '— '}cannot be changed after creation
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('user.email')} <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-input"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('user.firstName')} <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('user.lastName')} <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      {t('user.status')} — {formData.isActive ? t('user.active') : t('user.inactive')}
                    </label>
                  </div>

                  {outlets.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">
                        {t('user.outlet')}
                        {rolesRequireOutlet(roles, formData.roleIds) && <span className="required"> *</span>}
                      </label>
                      <select
                        className="form-select"
                        value={formData.outletId ?? ''}
                        onChange={(e) => setFormData((prev) => ({ ...prev, outletId: e.target.value ? parseInt(e.target.value) : null }))}
                      >
                        <option value="">{t('user.outletNotAssigned')}</option>
                        {outlets.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                      <small className="form-helper">{t('user.outletHelper')}</small>
                    </div>
                  )}

                  {roles.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">{t('user.roles')}</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                        {roles.map((role) => (
                          <label
                            key={role.id}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}
                          >
                            <input
                              type="checkbox"
                              checked={formData.roleIds.includes(role.id)}
                              onChange={() => handleRoleToggle(role.id)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <span style={{ fontWeight: 500 }}>{role.name}</span>
                            {role.description && (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>— {role.description}</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                </form>
              </div>
            </div>
          </div>

          <div className="create-sidebar">
            {companyApps.length > 0 && (
              <div className="create-card">
                <div className="create-card-header">
                  <h3 className="create-card-title">{t('user.appAccess')}</h3>
                </div>
                <div className="create-card-body">
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', marginTop: 0 }}>
                    Select which modules this user can access after login.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {companyApps.map((appCode) => {
                      const isSelected = selectedApps.includes(appCode);
                      return (
                        <label
                          key={appCode}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            cursor: 'pointer', padding: '10px 12px', borderRadius: '8px',
                            border: `1px solid ${isSelected ? 'var(--ims-accent, #059669)' : 'var(--ims-border, #e5e7eb)'}`,
                            background: isSelected ? 'var(--ims-accent-bg, #f0fdf4)' : 'transparent',
                            transition: 'all 0.15s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleAppToggle(appCode)}
                            style={{ width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                          />
                          <span style={{ fontSize: '18px', lineHeight: 1 }}>{APP_ICONS[appCode] ?? '📦'}</span>
                          <span style={{ fontWeight: 600, fontSize: '13px', color: isSelected ? 'var(--ims-accent, #059669)' : 'var(--ims-text-main, #111827)' }}>
                            {APP_LABELS[appCode] ?? appCode}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">PIN Login</h3>
              </div>
              <div className="create-card-body">
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', marginTop: 0 }}>
                  {user.hasPin
                    ? 'This user has a PIN set for waiter/cashier shared-device login.'
                    : 'No PIN set — this user cannot use the PIN login screen.'}
                </p>
                {pinError && (
                  <div className="alert alert-danger" style={{ fontSize: 12, padding: '8px 10px', marginBottom: 10 }}>{pinError}</div>
                )}
                {pinSuccess && (
                  <div className="alert alert-success" style={{ fontSize: 12, padding: '8px 10px', marginBottom: 10 }}>PIN updated.</div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-input"
                    placeholder={user.hasPin ? 'New 4-6 digit PIN' : 'Set 4-6 digit PIN'}
                    value={pinValue}
                    maxLength={6}
                    onChange={(e) => {
                      setPinValue(e.target.value.replace(/\D/g, ''));
                      setPinError(null);
                      setPinSuccess(false);
                    }}
                  />
                  <button
                    type="button"
                    className="ims-btn ims-btn-secondary"
                    onClick={handleSetPin}
                    disabled={pinSaving || pinValue.length < 4}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {pinSaving ? '…' : user.hasPin ? 'Change' : 'Set PIN'}
                  </button>
                </div>
              </div>
            </div>

            <div className="create-card">
              <div className="create-card-header">
                <h3 className="create-card-title">{t('user.auditInformation')}</h3>
              </div>
              <div className="create-card-body">
                <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{t('user.userId')}:</span>{' '}
                    <span style={{ color: '#6b7280' }}>{user.id}</span>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{t('user.status')}:</span>{' '}
                    <span className={`badge ${user.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {user.isActive ? t('user.active') : t('user.inactive')}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{t('user.lastLogin')}:</span>{' '}
                    <span style={{ color: '#6b7280' }}>
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString()
                        : t('user.neverLoggedIn')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UserEditPage;
